export const POSITION_SIZING_OUTPUT_HEATMAP_FIELDS = Object.freeze([
    'idio_vol_dollar',
    'idio_var_pct',
    'liquidity_factor_pct',
    'pos_gmv_base_pct',
    'pos_size_idio_adj_pct',
    'adtv_pct',
    'edge_dollar',
]);

const HEATMAP_LEVELS = 8;

function finiteNumber(value) {
    if (value == null || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}

function percentile(sortedValues, percentileValue) {
    const index = (sortedValues.length - 1) * percentileValue;
    const lowerIndex = Math.floor(index);
    const upperIndex = Math.ceil(index);
    if (lowerIndex === upperIndex) return sortedValues[lowerIndex];
    const weight = index - lowerIndex;
    return sortedValues[lowerIndex]
        + ((sortedValues[upperIndex] - sortedValues[lowerIndex]) * weight);
}

/**
 * Heat-map levels for the calculated output columns of the Position Sizing
 * tables: per field, a Map from row id to `{ tone, level }` (level 1-8).
 * Signed outputs use a zero-centred two-colour scale (tone 'negative' or
 * 'positive', by magnitude up to the 95th percentile); non-negative outputs
 * one increasing 'sequential' tone between the 5th and 95th percentiles.
 * Placeholder, new and deleted rows, uniform fields and blank values get no
 * level. pos_sizing_table/outputHeatmapClasses.js turns a level into the
 * cell's tint.
 */
export function buildPositionSizingOutputHeatmap(
    rows,
    fields = POSITION_SIZING_OUTPUT_HEATMAP_FIELDS,
) {
    const eligibleRows = (Array.isArray(rows) ? rows : []).filter((row) => (
        row?.id != null
        && !row?._isPlaceholder
        && !row?.isNewRow
        && !row?.toDelete
    ));
    const heatmap = new Map();

    fields.forEach((field) => {
        const entries = eligibleRows.map((row) => ({
            id: row.id,
            value: finiteNumber(row[field]),
        })).filter((entry) => entry.value != null);
        if (entries.length < 2) return;

        const levelsByRow = new Map();
        const sortedValues = entries.map((entry) => entry.value).sort((a, b) => a - b);
        if (sortedValues[0] === sortedValues[sortedValues.length - 1]) return;

        if (sortedValues[0] < 0) {
            const magnitudes = entries.map(({ value }) => Math.abs(value)).sort((a, b) => a - b);
            const magnitudeCap = percentile(magnitudes, 0.95) || magnitudes[magnitudes.length - 1];
            if (!(magnitudeCap > 0)) return;
            entries.forEach(({ id, value }) => {
                if (value === 0) return;
                const normalized = Math.min(1, Math.abs(value) / magnitudeCap);
                const level = Math.max(1, Math.ceil(normalized * HEATMAP_LEVELS));
                levelsByRow.set(id, { tone: value < 0 ? 'negative' : 'positive', level });
            });
        } else {
            const lowerBound = percentile(sortedValues, 0.05);
            const upperBound = percentile(sortedValues, 0.95);
            if (!(upperBound > lowerBound)) return;
            entries.forEach(({ id, value }) => {
                const normalized = Math.min(
                    1,
                    Math.max(0, (value - lowerBound) / (upperBound - lowerBound)),
                );
                if (normalized <= 0) return;
                const level = Math.max(1, Math.ceil(normalized * HEATMAP_LEVELS));
                levelsByRow.set(id, { tone: 'sequential', level });
            });
        }
        if (levelsByRow.size > 0) heatmap.set(field, levelsByRow);
    });

    return heatmap;
}
